const localeMap = {
  en: 'en-US',
  ru: 'ru-RU',
  hy: 'hy-AM',
};

// Armenian month names
const armenianMonths = [
  'հունվարի',
  'փետրվարի',
  'մարտի',
  'ապրիլի',
  'մայիսի',
  'հունիսի',
  'հուլիսի',
  'օգոստոսի',
  'սեպտեմբերի',
  'հոկտեմբերի',
  'նոյեմբերի',
  'դեկտեմբերի',
];

/**
 * Check if a locale is supported by the browser
 */
function isLocaleSupported(locale) {
  try {
    new Intl.DateTimeFormat(locale).format(new Date());
    return Intl.DateTimeFormat.supportedLocalesOf([locale]).length > 0;
  } catch {
    return false;
  }
}

/**
 * Format date manually for Armenian locale
 */
function formatArmenianDate(date, includeTime = false) {
  const day = date.getDate();
  const month = armenianMonths[date.getMonth()];
  const year = date.getFullYear();

  let result = `${day} ${month} ${year} թ.`;

  if (includeTime) {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    result += `, ${hours}:${minutes}`;
  }

  return result;
}

/**
 * Formats a date string according to the provided locale
 * @param {string|Date} dateString - The date to format
 * @param {string} locale - The locale to use (en, ru, hy)
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string or '-' if date is invalid
 */
export function formatDate(dateString, locale = 'en', options = {}) {
  if (!dateString) {
    return '-';
  }

  const date = dateString instanceof Date ? dateString : new Date(dateString);

  // Check if date is valid
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  const localeString = localeMap[locale] || 'en-US';

  // Use manual formatting for Armenian if not supported by browser
  if (locale === 'hy' && !isLocaleSupported(localeString)) {
    return formatArmenianDate(date, false);
  }

  const defaultOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };

  return date.toLocaleDateString(localeString, {
    ...defaultOptions,
    ...options,
  });
}

/**
 * Formats a date string with time according to the provided locale
 * @param {string|Date} dateString - The date to format
 * @param {string} locale - The locale to use (en, ru, hy)
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date and time string or '-' if date is invalid
 */
export function formatDateTime(dateString, locale = 'en', options = {}) {
  if (!dateString) {
    return '-';
  }

  const date = dateString instanceof Date ? dateString : new Date(dateString);

  // Check if date is valid
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  const localeString = localeMap[locale] || 'en-US';

  // Use manual formatting for Armenian if not supported by browser
  if (locale === 'hy' && !isLocaleSupported(localeString)) {
    return formatArmenianDate(date, true);
  }

  const defaultOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };
  return date.toLocaleString(localeString, {
    ...defaultOptions,
    ...options,
  });
}
